<!--
  The jump bar: one field, opened with the jump key (⌘K / Ctrl+K) or the
  top bar's button, that reaches any pull request, branch, compare,
  history or repository (docs/design.md, "Jump bar"). What is typed is
  read by the router's own functions (jump/interpret.ts) and completed by
  the sources (jump/sources.ts), merged by jump/complete.ts; an empty field
  lists the pull requests waiting on you, then recents, then pins.

  The WAI-ARIA combobox with a listbox popup: the focus stays in the
  field, which points at the active option (`aria-activedescendant`).
  Each result is one option, named with its title and the action Enter
  takes; the sources' sections are the listbox's groups, labelled by their
  headings. The arrows move between results, Tab between a result's
  actions (keys/controls.ts `jumpBarKeys`), said in a polite region of its
  own; the count has another. Enter on a guess the text made, while a
  source that may replace it is still asked, waits for it (at most
  FIND_MS), saying so. One instance, in App.
-->
<script lang="ts">
  import { tick, untrack } from 'svelte';
  import { nav } from '../nav.svelte';
  import { session } from '../state/session.svelte';
  import { jumpUi, places as appPlaces } from '../jump/jump.svelte';
  import { MAX_PINS, type Places } from '../jump/places.svelte';
  import { Completer, flatRows, moveCursor, settleCursor, type Cursor, type Schedule, type Section, type Source } from '../jump/complete';
  import { appSources } from '../jump/github';
  import { routeRepo } from '../jump/interpret';
  import type { Action, Row } from '../jump/rows';
  import { subjectKey } from '../subject';
  import { controlCommand } from '../keys/control';
  import { jumpBarKeys } from '../keys/controls';
  import { EnterWait } from '../jump/enterWait.svelte';

  interface Props {
    /** The signed-in user's recents and pins (the app's by default). */
    places?: Places;
    /** What completes the text (the app's sources by default, over `places`, with what is plugged in). */
    sources?: Source[];
    /** The debounces' and Enter's wait's clock (`setTimeout` by default). */
    schedule?: Schedule;
  }
  let { places = appPlaces, sources, schedule }: Props = $props();

  const timeout: Schedule = (run, ms) => {
    const t = setTimeout(run, ms);
    return () => clearTimeout(t);
  };
  const clock = untrack(() => schedule ?? timeout);

  let sections: Section[] = $state.raw([]);
  let asking: string[] = $state.raw([]);
  let failed: string[] = $state.raw([]);
  let cursor: Cursor | null = $state(null);
  let text = $state('');
  /** How many results there are, said when it changes. */
  let counted = $state('');
  /** What a pin did. */
  let said = $state('');
  /** What had the focus when the bar opened, given it back when it closes. */
  let before: HTMLElement | null = null;
  let dialog: HTMLDialogElement | undefined = $state();
  let field: HTMLInputElement | undefined = $state();
  /** The keys moved the cursor since the text last changed: it stays on its row as the list changes; else it is on the first. */
  let moved = false;

  const rows = $derived(flatRows(sections));
  const repo = $derived(routeRepo(nav.route));
  const here = $derived('subject' in nav.route ? subjectKey(nav.route.subject) : jumpUi.here);

  function make(): Completer {
    const c: Completer = new Completer({
      sources:
        sources ??
        appSources({
          context: () => ({ origin: location.origin, githubWeb: session.config?.github_web_url ?? null }),
          me: () => session.user?.login ?? '',
          recents: () => places.recents,
          pins: () => places.pins,
        }),
      schedule: clock,
      onChange: () => {
        if (c !== completer) return;
        // The page shown is no place to jump to, whichever source lists it.
        const shown = untrack(() => here);
        sections = c.sections.flatMap((s) => {
          const rows = s.rows.filter((r) => r.id !== shown);
          return rows.length ? [{ ...s, rows }] : [];
        });
        asking = c.asking;
        failed = c.failed;
        const rows = flatRows(sections);
        cursor = settleCursor(rows, moved ? cursor : null);
        // The options a screen reader numbers are the choices (each action is one): said beside the results when they differ.
        const choices = rows.reduce((n, r) => n + r.actions.length, 0);
        const results = rows.length === 1 ? '1 result' : `${rows.length} results`;
        const n = rows.length === 0 ? 'No results' : choices === rows.length ? results : `${results}, ${choices} choices`;
        if (n !== counted) counted = n;
        wait.changed(c.settled);
      },
    });
    return c;
  }
  // Made afresh on each opening, so a source plugged in since is asked.
  let completer: Completer = untrack(() => make());

  function ask() {
    completer.ask(text, repo);
  }

  function oninput() {
    moved = false;
    wait.stop();
    ask();
  }

  $effect(() => {
    if (!dialog) return;
    if (jumpUi.open && !dialog.open) {
      untrack(() => {
        before = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
        wait.stop();
        text = '';
        cursor = null;
        moved = false;
        counted = '';
        said = '';
        places.refresh();
        completer.close();
        completer = make();
        completer.reset();
        ask();
      });
      dialog.showModal();
      void tick().then(() => field?.focus());
    } else if (!jumpUi.open && dialog.open) {
      untrack(closed);
      dialog.close();
    }
  });

  /**
   * The bar is closed, however it was (Esc, a click outside, the jump key,
   * a choice): no wait goes on, nothing is asked, and the focus goes back.
   * Esc closes the dialog before its `close` event comes, so `cancel`
   * does this at once, and the bar's state follows the dialog's.
   */
  function closed() {
    wait.stop();
    completer.close();
    if (jumpUi.open) jumpUi.close();
    const to = before;
    before = null;
    if (to?.isConnected) to.focus({ preventScroll: true });
  }

  const pinned = (a: Extract<Action, { kind: 'pin' }>) => places.isPinned(a.place.key);
  const label = (a: Action) => (a.kind === 'go' ? a.label : pinned(a) ? 'Unpin' : 'Pin');
  const optionId = (row: number, action: number) => `jump-option-${row}-${action}`;
  const sectionId = (s: Section) => `jump-section-${s.id.replace(/[^\w-]/g, '-')}`;

  const at = $derived.by(() => {
    if (!cursor) return null;
    const i = rows.findIndex((r) => r.id === cursor!.id);
    return i < 0 ? null : { i, row: rows[i], action: cursor.action };
  });
  const activeId = $derived(at ? optionId(at.i, at.action) : undefined);
  /** An option's name: a result's first, its result then its action; each other action, the action then its result. */
  function named(row: Row, ai: number): string {
    const what = `${row.title}${row.detail ? ', ' + row.detail : ''}`;
    return ai === 0 ? `${what}: ${label(row.actions[0])}` : `${label(row.actions[ai])}: ${what}`;
  }

  // The active option in view: the focus stays in the field, so the browser does not scroll for it.
  $effect(() => {
    const id = activeId;
    if (!id) return;
    void tick().then(() => document.getElementById(id)?.scrollIntoView?.({ block: 'nearest' }));
  });

  function take(row: Row, a: Action) {
    if (a.kind === 'pin') {
      if (pinned(a)) {
        places.unpin(a.place.key);
        said = `Unpinned ${row.title}`;
      } else said = places.pin(a.place) ? `Pinned ${row.title}` : `${MAX_PINS} pins already: unpin one to pin ${row.title}`;
      ask();
      return;
    }
    if (a.remember) places.visit(a.remember);
    jumpUi.close();
    nav.go(a.href);
  }

  function takeActive() {
    if (at) take(at.row, at.row.actions[at.action]);
  }

  /** Whether Enter may take the active row now: no source still asked may replace it. */
  const sure = () => completer.settled || (!!at && !at.row.provisional);

  /** Enter: the active action, unless it is a guess a source still asked may replace (jump/enterWait.svelte.ts). */
  // A wait forgets what a pin said, so the region never says it again once the wait ends.
  const wait = new EnterWait({ schedule: clock, sure, flush: () => completer.flush(), take: takeActive, onwait: () => (said = '') });

  function onkeydown(e: KeyboardEvent) {
    // The jump key toggles the bar, as the dialog is: between Esc's cancel and its close it is already closed, and opens again.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (dialog?.open) jumpUi.close();
      else jumpUi.show();
      return;
    }
    const c = controlCommand(jumpBarKeys, e);
    // Esc is the dialog's own: it closes it (oncancel).
    if (!c || c === 'close') return;
    if (c === 'take') {
      e.preventDefault();
      wait.enter(text);
      return;
    }
    if (!rows.length) return;
    e.preventDefault();
    // Moving through the results is choosing: a waiting Enter is forgotten.
    wait.stop();
    cursor = moveCursor(rows, cursor, c);
    moved = true;
  }

  /** A click (or a screen reader's activation) on an option: that choice, once; a waiting Enter is forgotten. */
  function choose(i: number, ai: number) {
    wait.stop();
    const row = rows[i];
    cursor = { id: row.id, action: ai };
    moved = true;
    take(row, row.actions[ai]);
  }

  const placeholder = $derived(repo ? `A branch, #12, main...feat/x, ref//path, a URL, or owner/repo` : 'owner/repo, a URL, or a pull request on your dashboard');
</script>

<dialog
  class="sheet jump"
  bind:this={dialog}
  aria-label="Jump to"
  oncancel={closed}
  onclose={() => {
    // A close that comes after the bar opened again (the jump key right after Esc) closes nothing.
    if (!dialog?.open) closed();
  }}
  onclick={(e) => {
    if (e.target === dialog) jumpUi.close();
  }}
>
  {#if jumpUi.open}
    <input
      bind:this={field}
      bind:value={text}
      {oninput}
      {onkeydown}
      class="jump-field"
      type="text"
      role="combobox"
      aria-label="Jump to"
      aria-expanded={rows.length > 0}
      aria-controls="jump-results"
      aria-haspopup="listbox"
      aria-autocomplete="list"
      aria-activedescendant={activeId}
      aria-busy={wait.finding ? 'true' : undefined}
      autocomplete="off"
      autocapitalize="off"
      spellcheck="false"
      {placeholder}
    />
    <div id="jump-results" class="jump-results" role="listbox" aria-label="Results">
      {#each sections as s (s.id)}
        <ul role="group" aria-labelledby={sectionId(s)}>
          <li role="presentation" id={sectionId(s)} class="jump-head">{s.title}</li>
          {#each s.rows as row (row.id)}
            {@const i = rows.indexOf(row)}
            <!-- Each action is an option, so every one is reached by a touch screen reader as well as by Tab: the result's first carries its title. -->
            <!-- svelte-ignore a11y_click_events_have_key_events (the field's keys choose an option: the focus never leaves the field) -->
            <li role="option" id={optionId(i, 0)} tabindex="-1" class="jump-row" class:active={at?.i === i} class:current={at?.i === i && at.action === 0} aria-selected={at?.i === i && at.action === 0} aria-label={named(row, 0)} data-action={label(row.actions[0])} onmousedown={(e) => e.preventDefault()} onclick={() => choose(i, 0)}>
              <span class="jump-what"><span class="jump-title">{row.title}</span>{#if row.detail}<span class="jump-detail">{row.detail}</span>{/if}</span>
              <span class="jump-action" aria-hidden="true">{label(row.actions[0])}</span>
            </li>
            {#each row.actions.slice(1) as a, k (k)}
              <!-- svelte-ignore a11y_click_events_have_key_events (as above) -->
              <li role="option" id={optionId(i, k + 1)} tabindex="-1" class="jump-alt" class:active={at?.i === i} class:current={at?.i === i && at.action === k + 1} aria-selected={at?.i === i && at.action === k + 1} aria-label={named(row, k + 1)} data-action={label(a)} onmousedown={(e) => e.preventDefault()} onclick={() => choose(i, k + 1)}>{label(a)}</li>
            {/each}
            <li role="presentation" class="jump-end" class:active={at?.i === i}></li>
          {/each}
        </ul>
      {/each}
    </div>
    <p class="jump-foot">
      {#if wait.finding}<span class="jump-finding">{wait.foot}</span>{:else if asking.length}<span>Asking GitHub: {asking.join(', ')}…</span>{/if}
      {#if failed.length}<span class="jump-failed">Could not read: {failed.join(', ')}</span>{/if}
      {#if !rows.length && text.trim() && !asking.length}<span>Nothing matches{repo ? '' : '. In a repository, branch names, #12 and main...feat/x work too'}.</span>{/if}
      <span class="jump-keys"><kbd>↑</kbd><kbd>↓</kbd> results · <kbd>Tab</kbd> actions · <kbd>Enter</kbd> go · <kbd>Esc</kbd> close</span>
    </p>
  {/if}
</dialog>
<!-- Outside the dialog, which takes what it holds out of the accessibility tree while closed: in the page always, so what they say is heard as it changes. -->
<div class="sr-only jump-count" role="status">{counted}</div>
<div class="sr-only jump-said" aria-live="polite">{wait.said || said}</div>

<style>
  dialog.jump {
    margin-top: 12vh;
    padding: 0;
    max-width: 640px;
    overflow: hidden;
  }
  .jump-field {
    display: block;
    width: 100%;
    padding: 14px 16px;
    border: 0;
    border-bottom: 1px solid var(--line);
    background: transparent;
    color: var(--fg);
    font: 15px ui-monospace, monospace;
    outline: none;
  }
  .jump-results {
    padding: 4px 0;
    max-height: 55vh;
    overflow: auto;
  }
  .jump-results:empty {
    display: none;
  }
  .jump-results ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .jump-head {
    padding: 8px 16px 2px;
    font-size: 11px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .jump-results ul {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
  .jump-head {
    flex-basis: 100%;
  }
  /* A result: its first option (its title and first action), then its other actions, then the line's end. */
  .jump-row {
    flex: 1 1 60%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px 10px;
    padding: 6px 4px 6px 16px;
    cursor: pointer;
  }
  .jump-alt {
    flex: none;
    margin: 0 2px;
    padding: 2px 8px;
    border: 1px solid var(--line);
    border-radius: 30px;
    font-size: 11px;
    color: var(--muted);
    white-space: nowrap;
    cursor: pointer;
  }
  .jump-end {
    flex-basis: 100%;
    height: 0;
  }
  .jump-row.active,
  .jump-alt.active {
    background: var(--soft);
  }
  .jump-end.active {
    height: 2px;
    background: var(--soft);
  }
  .jump-what {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    gap: 8px;
    align-items: baseline;
  }
  .jump-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
  }
  .jump-detail {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: 11px ui-monospace, monospace;
    color: var(--muted);
  }
  .jump-action {
    flex: none;
    padding: 2px 8px;
    border: 1px solid var(--line);
    border-radius: 30px;
    font-size: 11px;
    color: var(--muted);
    white-space: nowrap;
  }
  .jump-row:hover .jump-action,
  .jump-alt:hover {
    border-color: var(--accent);
    color: var(--fg);
  }
  .jump-row.current .jump-action,
  .jump-alt.current {
    border-color: var(--accent);
    background: var(--accent);
    color: var(--surface);
  }
  .jump-foot {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin: 0;
    padding: 8px 16px;
    border-top: 1px solid var(--line);
    font-size: 11px;
  }
  dialog.sheet .jump-foot {
    margin-top: 0;
    font-size: 11px;
  }
  .jump-failed {
    color: var(--bad, var(--fg));
  }
  .jump-keys {
    margin-left: auto;
  }
  .jump-keys kbd {
    font-size: 10px;
  }
  @media (max-width: 500px) {
    dialog.jump {
      margin-top: 16px;
    }
    .jump-row {
      flex-basis: 100%;
    }
    .jump-alt {
      margin-bottom: 4px;
    }
    .jump-row + .jump-alt {
      margin-left: 16px;
    }
  }
</style>
